import {
  faArrowRight,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faPen,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import type { FC, ReactNode } from "react";
import type { FlowCard, CardAction } from "../../api/dto/FlowCard";
import { useModal } from "../../components/layout/ModalProvider";
import { AddCardModal } from "../../modals/AddCardModal";
import { DeleteCardModal } from "../../modals/DeleteCardModal";
import { EditCardContentModal } from "../../modals/EditCardContentModal";
import { CardContentPreview, useDisplayLanguage } from "./CardContentPreview";
import { colorForActionType } from "./CardFlowEdge";
import { cardActionIconFor } from "./cardActionIcon";
import type { CardApi } from "./cardApi";

export const cardNodeWidth = 260;

export type CardNodeData = {
  cards: FlowCard[];
  card: FlowCard;
  api: CardApi;
  cardTypeOptions: Record<string, string>;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMoveColumn: (card: FlowCard, direction: -1 | 1) => void;
  onMoveInColumn: (card: FlowCard, direction: -1 | 1) => void;
  onCardUpdated: (card: FlowCard) => void;
  onCardDeleted: (uuid: string) => void;
  // See CardFlowEditor.tsx's cardsRef comment — the content-edit modal's body is captured once at
  // open() time, so its own props can go stale if other local state changes while it's open; this
  // always reaches the current cards for save-time lookups.
  getFreshCards: () => FlowCard[];
  // Set when this card has so many incoming actions that they're drawn as stubs (see Connector).
  incomingConnector: Connector | null;
  // This card's actions whose target has a connector: drawn as stubs instead of lines.
  outgoingStubs: { action: CardAction; connector: Connector }[];
  onEditAction: (actionId: string) => void;
};

export type ColumnHeaderNodeData = {
  columnId: number;
  stepOffset: number;
};

export type AddCardNodeData = {
  api: CardApi;
  cardTypeOptions: Record<string, string>;
  columnId: number;
  posInColumn: number;
  onCardCreated: (card: FlowCard) => void;
};

export type DropZoneNodeData =
  // A gap where a new step is inserted; `active` while the dragged card is over it.
  | { kind: "newColumn"; active: boolean }
  // The line between two cards of the target step where the dragged card will go.
  | { kind: "position" };

// A target (a card, or the completion placeholder) with so many incoming actions that they're drawn
// as stubs next to their source cards (OutgoingStubs) instead of lines across the board. Each stub
// and the target show the same letter, on the same color — the letter so pairs don't rely on color.
export type Connector = {
  letter: string;
  color: string;
  count: number;
};

export type CompletionNodeData = {
  incomingConnector: Connector | null;
};

export type OffsetStepsNodeData = {
  stepOffset: number;
};

// A card's visual: name, type, full content preview, move/edit/delete buttons — one target handle
// (left) and one source handle (right) for dragging action connections between cards.
const CardNode: FC<NodeProps> = (props) => {
  const data = props.data as unknown as CardNodeData;
  const displayLanguage = useDisplayLanguage();
  const deleteCardModal = useModal({
    title: "Kaart verwijderen",
    body: (
      <DeleteCardModal
        card={data.card}
        deleteCard={data.api.deleteCard}
        onDeleted={() => data.onCardDeleted(data.card.uuid)}
      />
    ),
  });
  const editContentModal = useModal({
    title: "Content bewerken",
    size: "2xl",
    body: (
      <EditCardContentModal
        cards={data.cards}
        card={data.card}
        api={data.api}
        cardTypeOptions={data.cardTypeOptions}
        onSaved={data.onCardUpdated}
        getFreshCards={data.getFreshCards}
      />
    ),
  });

  const cardName =
    data.card.name[displayLanguage] ?? Object.values(data.card.name)[0] ?? "(naamloos)";

  return (
    <div
      className={"relative bg-white border border-slate-200 rounded shadow-sm"}
      style={{ width: `${cardNodeWidth}px` }}
    >
      {data.incomingConnector && <IncomingConnector connector={data.incomingConnector} />}
      <OutgoingStubs stubs={data.outgoingStubs} onEdit={data.onEditAction} />
      <Handle
        type={"target"}
        position={Position.Left}
        id={"in"}
        title={"Sleep naar een andere kaart om ze te verbinden"}
      />
      <Handle
        type={"source"}
        position={Position.Right}
        id={"out"}
        title={"Sleep naar een andere kaart om ze te verbinden"}
      />
      <div className={"flex items-start justify-between w-full p-2"}>
        <div>{cardName}</div>
        <div className={"text-xs font-medium uppercase mt-1 text-slate-600"}>
          {data.cardTypeOptions[data.card.cardType] ?? data.card.cardType}
        </div>
      </div>
      <div className={"px-2 pb-2 text-xs text-slate-500 space-y-2"}>
        <CardContentPreview card={data.card} language={displayLanguage} />
      </div>
      <div className={"flex items-center justify-between border-t border-slate-100 px-1 py-1"}>
        <div className={"flex items-center"}>
          <NodeActionButton
            icon={faChevronLeft}
            title={"Naar vorige stap"}
            disabled={!data.canMoveLeft}
            onClick={() => data.onMoveColumn(data.card, -1)}
          />
          <NodeActionButton
            icon={faChevronUp}
            title={"Naar boven"}
            disabled={!data.canMoveUp}
            onClick={() => data.onMoveInColumn(data.card, -1)}
          />
          <NodeActionButton
            icon={faChevronDown}
            title={"Naar beneden"}
            disabled={!data.canMoveDown}
            onClick={() => data.onMoveInColumn(data.card, 1)}
          />
          <NodeActionButton
            icon={faChevronRight}
            title={"Naar volgende stap"}
            disabled={!data.canMoveRight}
            onClick={() => data.onMoveColumn(data.card, 1)}
          />
        </div>
        <div className={"flex items-center"}>
          <NodeActionButton
            icon={faPen}
            title={"Content bewerken"}
            onClick={() => editContentModal.open()}
          />
          <NodeActionButton
            icon={faTrash}
            title={"Verwijderen"}
            className={"text-red-600 hover:bg-red-50"}
            onClick={() => deleteCardModal.open()}
          />
        </div>
      </div>
    </div>
  );
};

// Just a label: steps follow the cards (see CardFlowEditor's saveLayout), so there's nothing to add or
// delete here — a step appears when a card is dropped between two steps and disappears with its last
// card.
const ColumnHeaderNode: FC<NodeProps> = (props) => {
  const data = props.data as unknown as ColumnHeaderNodeData;
  return (
    <div
      className={
        "border border-slate-200 bg-slate-50 font-medium text-xs py-1 px-2 text-slate-400 flex items-center h-8 rounded"
      }
      style={{ width: `${cardNodeWidth}px` }}
    >
      STAP {data.columnId + data.stepOffset + 1}
    </div>
  );
};

const AddCardNode: FC<NodeProps> = (props) => {
  const data = props.data as unknown as AddCardNodeData;
  const addCardModal = useModal({
    title: "Kaart toevoegen",
    body: (
      <AddCardModal
        column={data.columnId}
        posInColumn={data.posInColumn}
        createCard={data.api.createCard}
        cardTypeOptions={data.cardTypeOptions}
        onCreated={data.onCardCreated}
      />
    ),
  });

  return (
    <button
      className={
        "nodrag nopan hover:bg-slate-100 border-dashed hover:border-solid text-slate-600 border border-slate-200 rounded flex items-center justify-center min-h-12"
      }
      style={{ width: `${cardNodeWidth}px` }}
      type={"button"}
      onClick={() => addCardModal.open()}
    >
      <FontAwesomeIcon icon={faPlus} />
    </button>
  );
};

// Shown only while a card is being dragged (see CardFlowEditor's onNodeDrag): a dashed line with a
// "Nieuwe stap" pill in every gap between steps, sized to exactly the area where a drop inserts a step
// and drawn solid while it's the target; and a line in the target step where the card will go.
const DropZoneNode: FC<NodeProps> = (props) => {
  const data = props.data as unknown as DropZoneNodeData;
  if (data.kind === "position") {
    return <div className={"pointer-events-none w-full h-full rounded-full bg-brand-500"} />;
  }
  return (
    <div className={"pointer-events-none w-full h-full flex flex-col items-center"}>
      <span
        className={`rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${
          data.active
            ? "bg-brand-500 border-brand-500 text-white"
            : "bg-white border-slate-300 text-slate-400"
        }`}
      >
        <FontAwesomeIcon icon={faPlus} /> Nieuwe stap
      </span>
      <div
        className={`flex-1 border-l-2 ${data.active ? "border-brand-500" : "border-dashed border-slate-300"}`}
      />
    </div>
  );
};

// Header above a grayed-out placeholder card — no buttons.
const PlaceholderHeader: FC<{ label: string }> = (props) => (
  <div
    className={
      "border border-slate-200 bg-slate-50 font-medium text-xs py-1 px-2 text-slate-300 flex items-center h-8 rounded"
    }
    style={{ width: `${cardNodeWidth}px` }}
  >
    {props.label}
  </div>
);

const PlaceholderCard: FC<{ children?: ReactNode }> = (props) => (
  <div
    className={"bg-slate-100 border border-slate-200 rounded p-2 space-y-2 opacity-60"}
    style={{ width: `${cardNodeWidth}px` }}
  >
    {props.children}
    <div className={"h-3 w-2/3 rounded bg-slate-300"} />
    <div className={"h-2 w-full rounded bg-slate-200"} />
    <div className={"h-2 w-5/6 rounded bg-slate-200"} />
  </div>
);

// The steps that precede column 0 (see CardFlowEditor's `stepOffset`): a header and a single
// grayed-out card standing in for them. Its source handle is the flow's entry point — dragging it
// onto a card marks that card as the first one (see CardFlowEditor's onConnect).
const OffsetStepsHeaderNode: FC<NodeProps> = (props) => {
  const data = props.data as unknown as OffsetStepsNodeData;
  if (data.stepOffset === 0) return <PlaceholderHeader label={"START"} />;
  return (
    <PlaceholderHeader label={`STAP ${data.stepOffset === 1 ? "1" : `1-${data.stepOffset}`}`} />
  );
};

const OffsetStepsCardNode: FC<NodeProps> = () => (
  <PlaceholderCard>
    <Handle
      type={"source"}
      position={Position.Right}
      id={"out"}
      title={"Sleep naar een kaart om die als eerste kaart in te stellen"}
    />
  </PlaceholderCard>
);

// After the last column: where a flow ends. Dragging a card's action onto it creates an action without
// a next card (next_card_uuid null); those are drawn into it like any other target (see Connector).
const CompletionHeaderNode: FC<NodeProps> = () => <PlaceholderHeader label={"afronden"} />;

const CompletionCardNode: FC<NodeProps> = (props) => {
  const data = props.data as unknown as CompletionNodeData;
  return (
    <div className={"relative"}>
      {data.incomingConnector && <IncomingConnector connector={data.incomingConnector} />}
      <PlaceholderCard>
        <Handle
          type={"target"}
          position={Position.Left}
          id={"in"}
          title={"Sleep een kaart hierheen om daar af te ronden"}
        />
      </PlaceholderCard>
    </div>
  );
};

const ConnectorBadge: FC<{ connector: Connector }> = (props) => (
  <span
    className={
      "w-4 h-4 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
    }
    style={{ backgroundColor: props.connector.color }}
  >
    {props.connector.letter}
  </span>
);

// Left of a target with a connector: its letter and how many actions arrive through it, with a short
// arrow into the target's handle.
const IncomingConnector: FC<{ connector: Connector }> = (props) => (
  <div
    className={"absolute right-full top-1/2 -translate-y-1/2 flex items-center"}
    title={`${props.connector.count} acties komen hier binnen`}
  >
    <span
      className={
        "h-6 bg-white border rounded-full px-1.5 text-xs font-medium text-slate-700 flex items-center gap-1.5"
      }
      style={{ borderColor: props.connector.color }}
    >
      <ConnectorBadge connector={props.connector} />
      {props.connector.count}
    </span>
    <svg width={48} height={10} viewBox={"0 0 48 10"} aria-hidden>
      <line x1={0} y1={5} x2={40} y2={5} stroke={props.connector.color} strokeWidth={2} />
      <path d={"M40 0 L48 5 L40 10 Z"} fill={props.connector.color} />
    </svg>
  </div>
);

// Right of a card: its actions whose target has a connector, each a short line into a label ending in
// the target's letter, instead of a line across the board. Clicking one edits the action.
const OutgoingStubs: FC<{
  stubs: { action: CardAction; connector: Connector }[];
  onEdit: (actionId: string) => void;
}> = (props) => (
  <div className={"absolute left-full top-1/2 -translate-y-1/2 flex flex-col gap-2"}>
    {props.stubs.map(({ action, connector }) => {
      const color = colorForActionType(action.actionType);
      const icon = cardActionIconFor(action.answerValue);
      return (
        <button
          key={action.id}
          type={"button"}
          className={"nodrag nopan flex items-center"}
          title={icon ? "Klik om te bewerken" : `${action.answerValue} — klik om te bewerken`}
          onClick={() => props.onEdit(action.id)}
        >
          <span className={"w-12 h-0.5"} style={{ backgroundColor: color }} />
          <span
            className={
              "h-6 bg-white border rounded-full px-2 text-xs font-medium text-slate-700 flex items-center gap-1.5 whitespace-nowrap"
            }
            style={{ borderColor: color }}
          >
            {icon ? (
              <FontAwesomeIcon icon={icon} style={{ color }} />
            ) : (
              <span className={"max-w-20 truncate"}>{action.answerValue}</span>
            )}
            <FontAwesomeIcon icon={faArrowRight} className={"text-slate-400"} />
            <ConnectorBadge connector={connector} />
          </span>
        </button>
      );
    })}
  </div>
);

const NodeActionButton: FC<{
  icon: typeof faTrash;
  title: string;
  disabled?: boolean;
  className?: string;
  onClick: () => void;
}> = (props) => {
  return (
    <button
      type={"button"}
      title={props.title}
      disabled={props.disabled}
      onClick={props.onClick}
      className={`nodrag nopan w-6 h-6 flex items-center justify-center rounded text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-25 disabled:hover:bg-transparent ${props.className ?? ""}`}
    >
      <FontAwesomeIcon icon={props.icon} />
    </button>
  );
};

export const cardFlowNodeTypes = {
  card: CardNode,
  columnHeader: ColumnHeaderNode,
  addCard: AddCardNode,
  dropZone: DropZoneNode,
  offsetStepsHeader: OffsetStepsHeaderNode,
  offsetStepsCard: OffsetStepsCardNode,
  completionHeader: CompletionHeaderNode,
  completionCard: CompletionCardNode,
};
